import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  IconButton,
  Container,
  Fade,
  Grow,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { CircularProgress } from "@mui/material";

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

const HandwrittenPrescriptionUpload = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");
  const patientId = urlParams.get("patient_id");
  const facility = urlParams.get("facility");
  const orgCode = urlParams.get("org_code");
  const appMode = urlParams.get("app_mode");

  const canUploadWithToken = Boolean(token);
  const canUploadLegacy =
    Boolean(patientId) &&
    Boolean(orgCode) &&
    Boolean(appMode) &&
    orgCode !== "undefined" &&
    appMode !== "undefined";

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => {
      const isValid = ["image/jpeg", "image/png", "application/pdf"].includes(
        file.type
      );
      if (!isValid) {
        toast.error(
          `${file.name} is not a valid file type. Use JPG, PNG or PDF.`
        );
      }
      return isValid;
    });

    const filePromises = validFiles.map(
      (file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () =>
            resolve({
              name: file.name,
              type: file.type,
              base64: reader.result,
              raw: file,
            });
          reader.readAsDataURL(file);
        })
    );

    Promise.all(filePromises).then((newFiles) => {
      setSelectedFiles((prev) => [...prev, ...newFiles]);
      if (newFiles.length > 0) {
        toast.info(`${newFiles.length} file(s) added.`);
      }
    });
    e.target.value = "";
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!canUploadWithToken && !canUploadLegacy) {
      toast.error(
        "Invalid link: missing secure token or session details. Please scan the handwritten-prescription QR from your doctor."
      );
      return;
    }

    if (selectedFiles.length === 0) {
      toast.warning("Please add at least one photo or PDF of the prescription.");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    // Backend expects: upload_type in ('voice', 'doc', 'prescription')
    formData.append("upload_type", "prescription");
    if (patientId) {
      formData.append("patient_id", patientId);
    }
    selectedFiles.forEach((f) => {
      formData.append("files", f.raw);
    });

    try {
      const cleanAxios = axios.create();

      let url = `${REACT_APP_BACKEND_ROUTE_URL}patient-qr-upload/?upload_type=prescription`;
      const headers = {
        "Content-Type": "multipart/form-data",
      };

      if (canUploadWithToken) {
        url = `${REACT_APP_BACKEND_ROUTE_URL}patient-qr-upload/?token=${encodeURIComponent(
          token
        )}&upload_type=prescription`;
        headers["QR-Token"] = token;
      } else {
        headers["Org-Code"] = orgCode || "";
        headers["App-Mode"] = appMode || "";
        headers["Patient-Id"] = patientId || "";
        headers["QR-Token"] = token || "";
        headers["User-Access-Key"] = "dummy_mobile_key";
        headers.Username = "mobile_user";
      }

      const response = await cleanAxios.post(url, formData, { headers });

      if (response.data?.status === "Success" || response.status === 200) {
        toast.success(
          response.data?.message ||
            "Handwritten prescription uploaded successfully!"
        );
        setSelectedFiles([]);
      } else {
        toast.error(
          response.data?.message || "Upload failed. Please try again."
        );
      }
    } catch (error) {
      console.error("Upload error:", error);
      const status = error.response?.status;
      const errorMessage =
        error.response?.data?.message ||
        "Error uploading. Please check your connection.";
      if (status === 401 || status === 403) {
        toast.error(
          errorMessage ||
            "Link expired or invalid. Please scan a new QR code from your doctor."
        );
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsUploading(false);
    }
  };

  if (!canUploadWithToken && !canUploadLegacy) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "#132140",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 2,
          px: 2,
        }}
      >
        <ToastContainer position="top-center" autoClose={3000} />
        <Container maxWidth="sm">
          <Card
            sx={{
              p: 4,
              borderRadius: 3,
              bgcolor: "#f8fafc",
              textAlign: "center",
              boxShadow: 3,
            }}
          >
            <EditNoteIcon sx={{ fontSize: 48, color: "#dc2626", mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#dc2626", mb: 1 }}>
              Invalid link
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Open this page from the handwritten-prescription QR code your doctor
              shared, or use a valid upload link.
            </Typography>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#132140",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <Container
        maxWidth={false}
        sx={{ width: { xs: "100%", sm: "95%", md: "600px" } }}
      >
        <Grow in timeout={800}>
          <Card
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
              bgcolor: "#f8fafc",
              boxShadow:
                "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: "#eef2ff",
                  border: "1px solid #d0d7f7",
                  mb: 1.5,
                }}
              >
                <EditNoteIcon sx={{ fontSize: 32, color: "#1e3a8a" }} />
              </Box>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: "#1e293b",
                  fontSize: { xs: "1.5rem", sm: "1.85rem" },
                }}
              >
                Handwritten Prescription Upload
              </Typography>
              {/* Intentionally removed long description to match compact mobile layout */}
              <Box
                sx={{
                  display: "inline-flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 0.75,
                  bgcolor: "#eef2ff",
                  px: 2.5,
                  py: 1,
                  borderRadius: 5,
                  border: "1px solid #d0d7f7",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#1e3a8a", fontWeight: 600, fontSize: "0.8rem" }}
                >
                  {token ? "Secure session" : `Patient: ${patientId || "—"}`}
                </Typography>
                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                  {facility ? "•" : ""}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#1e3a8a", fontWeight: 600, fontSize: "0.8rem" }}
                >
                  {facility || ""}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: 2,
                bgcolor: "#eef2ff",
                border: "1px solid #d0d7f7",
              }}
            >
              <Typography
                variant="caption"
                component="div"
                sx={{ color: "#1e3a8a", fontWeight: 700, mb: 0.5 }}
              >
                Tips for a clear upload
              </Typography>
              <Box
                component="ul"
                sx={{ color: "#334155", m: 0, pl: 2.25, fontSize: "0.75rem", lineHeight: 1.5 }}
              >
                <Box component="li" sx={{ mb: 0.25 }}>
                  Hold the phone parallel to the paper — avoid strong angle.
                </Box>
                <Box component="li" sx={{ mb: 0.25 }}>
                  Use bright, even light; avoid shadow over the writing.
                </Box>
                <Box component="li">Include the full page, including header and doctor sign/stamp.</Box>
              </Box>
            </Box>

            <Box
              sx={{
                p: { xs: 3, sm: 5 },
                border: "2px dashed #94a3b8",
                borderRadius: 3,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: "#e2e8f0",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#cbd5e1",
                  borderColor: "#64748b",
                },
                "&:active": { transform: "scale(0.99)" },
                mb: 2,
              }}
              onClick={() => document.getElementById("rx-file-input").click()}
            >
              <CloudUploadIcon
                sx={{ fontSize: { xs: 56, sm: 64 }, color: "#1e293b", mb: 1.5 }}
              />
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}
              >
                Click To Add Photos
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                Multiple selection supported (JPG, PNG, PDF)
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <label htmlFor="rx-file-input" style={{ width: "100%", cursor: "pointer" }}>
                  <Button
                    fullWidth
                    component="span"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: "#cbd5e1",
                      color: "#1e293b",
                      "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" },
                    }}
                  >
                    Choose files
                  </Button>
                </label>
              </Grid>
              <Grid item xs={12} sm={6}>
                <label htmlFor="rx-camera-input" style={{ width: "100%", cursor: "pointer" }}>
                  <Button
                    fullWidth
                    component="span"
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: "#cbd5e1",
                      color: "#1e293b",
                      "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" },
                    }}
                  >
                    Take photo
                  </Button>
                </label>
              </Grid>
            </Grid>

            <input
              id="rx-file-input"
              type="file"
              multiple
              accept="image/*,application/pdf"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <input
              id="rx-camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {selectedFiles.length > 0 && (
              <Fade in>
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Box
                    sx={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 1.5,
                    }}
                  >
                    {selectedFiles.map((file, index) => (
                      <Grow in key={`${file.name}-${index}`} timeout={300 + index * 80}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 1.5,
                            border: "1px solid #e2e8f0",
                            borderRadius: 2,
                            bgcolor: "#fff",
                          }}
                        >
                          <InsertDriveFileIcon
                            sx={{ color: "#64748b", mr: 1.5, fontSize: "1.5rem" }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              flexGrow: 1,
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "0.85rem",
                              color: "#334155",
                            }}
                          >
                            {file.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(index);
                            }}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grow>
                    ))}
                  </Box>
                </Box>
              </Fade>
            )}

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleUpload}
              sx={{
                mt: selectedFiles.length > 0 ? 1 : 3,
                py: 2,
                borderRadius: 2,
                bgcolor: selectedFiles.length > 0 ? "#1e293b" : "#e2e8f0",
                color: selectedFiles.length > 0 ? "#fff" : "#94a3b8",
                "&:hover": {
                  bgcolor: selectedFiles.length > 0 ? "#0f172a" : "#e2e8f0",
                },
                "&.Mui-disabled": {
                  bgcolor: "#e2e8f0",
                  color: "#94a3b8",
                },
                fontSize: "1rem",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "none",
              }}
              disabled={selectedFiles.length === 0 || isUploading}
              startIcon={
                isUploading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : null
              }
            >
              {isUploading ? "Uploading…" : "Upload prescription"}
            </Button>

            <Typography
              variant="caption"
              align="center"
              display="block"
              sx={{
                mt: 3,
                color: "#94a3b8",
                fontStyle: "italic",
                fontSize: "0.75rem",
              }}
            >
              Secure upload powered by Hlthtek™ EMR
            </Typography>
          </Card>
        </Grow>
      </Container>
    </Box>
  );
};

export default HandwrittenPrescriptionUpload;
